import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import './QRCinematicResponse.css';
import { GoldLeafFlash } from './GoldLeafFlash';
import { getAudioService } from '../../services/audio/AudioService';
import { useAtmosphereSettings } from '../../hooks/useAtmosphereSettings';
import {
  getDeadpanLine as defaultGetDeadpanLine,
  getFlavorString as defaultGetFlavorString,
} from '../../lib/qrFlavorStrings';

/**
 * Atmosphere A3 — QR cinematic response.
 *
 * Renders after the D100 tumble settles, presenting the quality outcome
 * (Excellent / Very Good / Good / Acceptable / Failure) with a flavor
 * string and a QR-keyed audio sting. Five distinct visual treatments:
 *   - QR 1 (Excellent):  Gold-leaf flash overlay + bright gold border +
 *                        brass-swell SFX. The peak emotional beat.
 *   - QR 2 (Very Good):  Softer gold-leaf flash (0.3 intensity) + warm
 *                        gold border + same brass at lower volume.
 *   - QR 3 (Good):       No flash. Standard gold border + subtle gold
 *                        glow pulse. Tonal SFX still.
 *   - QR 4 (Acceptable): No flash. Desaturated gunmetal border. Dry
 *                        sub-thunk SFX.
 *   - Failure (null):    No flash. Muted red border. Same dry thunk
 *                        with longer decay tail.
 *
 * Voice-mode differentials (Slice B, 2026-06-03):
 *   - QR 1 + voice: gold flash extended to 1.5× (so the brass-swell
 *     "lands" the moment fully); pre-flavor "Confirmed — Excellent."
 *     stage displays for the first 700ms before the flavor string
 *     fades in; playConfirmedChime fires alongside the brass-swell.
 *   - QR 4 + voice / Failure + voice: deadpan line is sampled from the
 *     dedicated `deadpan_voice_lines` pool and appended below the
 *     flavor string in slightly larger type for legibility-at-table.
 *   - QR 2 / QR 3 + voice: no visual differentials; the audio +0.15
 *     volume boost (in playQRSting) is the only differentiator.
 *
 * Flavor strings are skill-family-keyed in Slice B: `roll.skill_name`
 * is resolved against the seed's `skill_family_map` and the corresponding
 * bucket is preferred over generic. Unmapped skills fall back to generic.
 *
 * Lifecycle:
 *   - SFX (brass-swell or thunk) fires once on mount (ref-guarded for
 *     StrictMode). Voice + QR 1 ALSO fires playConfirmedChime in the
 *     same effect tick when the audioService exposes it.
 *   - Flavor string is picked once on mount (ref-guarded).
 *   - Deadpan line (voice + QR 4 / Failure only) is picked once on
 *     mount via the same ref-guard pattern.
 *   - Card animates in via framer-motion. onAnimationComplete drives
 *     `cardDone`; flash overlay (QR 1/2 only) drives `flashDone`. When
 *     BOTH are true, the component fires its `onComplete` prop, which
 *     ScenarioRunner uses to end the cinematic store + tear down.
 *
 * Audio is fire-and-forget. Audio failures are silent — the cinematic
 * never crashes the runner (same pattern as DiceRollTumble).
 */

export interface QRCinematicResponseProps {
  /**
   * The resolved roll. `quality_rating` is typed `number | null` to match
   * the broader `RollLog` shape; values outside 1-4 are narrowed to the
   * Failure visual + audio path defensively.
   */
  roll: {
    quality_rating: number | null;
    quality_name: string;
    /** Optional — `RollLog.skill_name` is `string | undefined`. Resolves
     *  to a family bucket via the seed's `skill_family_map` (Slice B). */
    skill_name?: string;
  };
  /** Fired once the cinematic has fully settled. Parent ends the store. */
  onComplete: () => void;
  /** Trigger path. Voice activates QR 1 chime + QR 4/Failure deadpan
   *  line + 1.5× gold-leaf flash extension for QR 1. */
  mode?: 'selector' | 'voice';
  /** Card animation duration in ms. If omitted, derived from quality_rating. */
  durationMs?: number;
  /** Flavor string getter (test injection). Signature matches the real
   *  loader: `(qr, rng?, skillName?) => string`. */
  getFlavorString?: (
    qr: 1 | 2 | 3 | 4 | null,
    rng?: () => number,
    skillName?: string,
  ) => string;
  /** Deadpan-line getter (test injection). Signature matches the real
   *  loader: `(qr, rng?) => string`. Returns '' for QR 1-3. */
  getDeadpanLine?: (qr: 1 | 2 | 3 | 4 | null, rng?: () => number) => string;
  /** AudioService hook (test injection; default = singleton). */
  audioService?: {
    playQRSting(
      qr: 1 | 2 | 3 | 4 | null,
      mode: 'selector' | 'voice',
      opts?: { volume?: number; durationMs?: number },
    ): Promise<void>;
    playConfirmedChime?(opts?: { volume?: number }): Promise<void>;
  };
  /** Optional testid prefix; default 'br-qr-cinematic-response'. */
  testidPrefix?: string;
}

// 2026-06-04 dice-feedback round 2: user reported "the message after
// also disappears" — the QR cinematic shown after the dice tumble was
// rendering the EXCELLENT / GOOD / ACCEPTABLE / FAILURE stamp for 800-
// 1500ms total, which is shorter than reaction time, so the GM never
// got to actually READ the outcome. Bumped 2-3× across the board.
// QR 1 (Excellent) gets the longest hold because it's the dramatic Bond
// moment; Failure also gets a generous 3s because the player needs to
// internalize a bad result before the result drawer takes over.
const DURATION_MS_BY_QR: Record<1 | 2 | 3 | 4 | 'null', number> = {
  1: 3500,
  2: 3000,
  3: 2800,
  4: 2500,
  null: 3000,
};

const FLASH_INTENSITY_BY_QR: Partial<Record<1 | 2 | 3 | 4, number>> = {
  1: 0.6,
  2: 0.3,
};

/** Slice B: gold-leaf flash extension for QR 1 + voice. */
const VOICE_QR1_FLASH_MULTIPLIER = 1.5;

/** Slice B: how long the "Confirmed — Excellent." stage holds before
 *  fading to the flavor string. */
const VOICE_QR1_CONFIRM_HOLD_MS = 700;

/** Slice B: text shown in the pre-flavor stage on QR 1 + voice. */
const VOICE_CONFIRM_TEXT_BY_QR: Partial<Record<1 | 2 | 3 | 4, string>> = {
  1: 'Confirmed — Excellent.',
};

function qrVariantKey(qr: 1 | 2 | 3 | 4 | null): 'qr1' | 'qr2' | 'qr3' | 'qr4' | 'failure' {
  if (qr === null) return 'failure';
  return (`qr${qr}` as const);
}

function narrowQr(raw: number | null): 1 | 2 | 3 | 4 | null {
  return raw === 1 || raw === 2 || raw === 3 || raw === 4 ? raw : null;
}

export function QRCinematicResponse({
  roll,
  onComplete,
  mode = 'selector',
  durationMs,
  getFlavorString = defaultGetFlavorString,
  getDeadpanLine = defaultGetDeadpanLine,
  audioService,
  testidPrefix = 'br-qr-cinematic-response',
}: QRCinematicResponseProps) {
  const qr = narrowQr(roll.quality_rating);
  const variantKey = qrVariantKey(qr);

  // A9 retrofit: gate the cinematic on the atmosphere settings store.
  // When qrCinematicsEnabled is off OR animations master switch is off,
  // skip the overlay entirely and fire onComplete immediately so the
  // parent's result reveal happens without a cinematic delay. SFX is
  // gated independently — when off, no audio fires.
  const {
    qrCinematicsEnabled,
    animationsEnabled,
    sfxEnabled,
    qrFlavorStringsEnabled,
    qrBrassSwellEnabled,
    voiceConfirmStingEnabled,
    cinematicDismissMode,
    cinematicHoldMs,
  } = useAtmosphereSettings();

  // 2026-06-05 cinematic-linger: in 'auto' mode, the visible duration is
  // max(per-QR baseline, user-controlled hold). Per-QR baselines stay as
  // a floor so QR1 doesn't accidentally land shorter than QR4 if a user
  // dials hold below the QR4 value. In 'manual' mode there is NO
  // animated duration — the card animates IN, then stays open until the
  // user clicks. We still pass a numeric duration to framer-motion so
  // the in-animation lands; the onComplete is gated on the click handler
  // below instead of the animation's onAnimationComplete.
  const perQrDefault = DURATION_MS_BY_QR[qr === null ? 'null' : qr];
  const autoLingerMs = Math.max(perQrDefault, cinematicHoldMs);
  const effectiveDurationMs = durationMs ?? autoLingerMs;
  const durationSeconds = effectiveDurationMs / 1000;
  const cinematicActive = qrCinematicsEnabled && animationsEnabled;
  const sfxActive = sfxEnabled;

  // Slice B: voice-mode triggers
  const isVoiceQR1 = mode === 'voice' && qr === 1;
  const isVoiceQR4OrFailure = mode === 'voice' && (qr === 4 || qr === null);
  const confirmText = isVoiceQR1 ? VOICE_CONFIRM_TEXT_BY_QR[1] : undefined;

  // Slice B: flash gets extended 1.5× for QR 1 + voice so the brass-swell
  // has time to land. Both flash + card share the SAME `cardDone`
  // gate, so extending the flash naturally pushes the onComplete out.
  const flashDurationMs = isVoiceQR1
    ? effectiveDurationMs * VOICE_QR1_FLASH_MULTIPLIER
    : effectiveDurationMs;

  const sfxFiredRef = useRef(false);
  const flavorRef = useRef<string | null>(null);
  if (flavorRef.current === null) {
    flavorRef.current = getFlavorString(qr, undefined, roll.skill_name);
  }
  const flavor = flavorRef.current;

  // Slice B: deadpan voice-line picked once on mount for voice +
  // QR 4/Failure. Empty string for all other (qr, mode) combos.
  const deadpanRef = useRef<string | null>(null);
  if (deadpanRef.current === null) {
    deadpanRef.current = isVoiceQR4OrFailure ? getDeadpanLine(qr) : '';
  }
  const deadpanLine = deadpanRef.current;

  useEffect(() => {
    if (sfxFiredRef.current) {
      return;
    }
    sfxFiredRef.current = true;
    if (!sfxActive) {
      // A9: SFX disabled — skip all audio cues.
      return;
    }
    const service = audioService ?? getAudioService();
    // A9: brass-swell sub-toggle gates the QR sting itself; when off,
    // the visual cinematic still fires (the brass is just the sonic
    // landing). qrBrassSwellEnabled is OFF only when the user explicitly
    // disables it OR Minimal mode is active.
    if (qrBrassSwellEnabled) {
      service
        .playQRSting(qr, mode, { durationMs: effectiveDurationMs })
        .catch(() => {
          // Audio failure silent — cinematic never crashes the runner.
        });
    }
    // Slice B: voice + QR 1 layers the "Confirmed — Excellent." chime
    // alongside the brass-swell. A9: voiceConfirmStingEnabled gates this
    // chime independently so a user can keep the brass but disable the
    // confirm sting.
    if (isVoiceQR1 && voiceConfirmStingEnabled && service.playConfirmedChime) {
      service.playConfirmedChime({ volume: 0.5 }).catch(() => {
        // Audio failure silent.
      });
    }
  }, [
    audioService,
    qr,
    mode,
    effectiveDurationMs,
    isVoiceQR1,
    sfxActive,
    qrBrassSwellEnabled,
    voiceConfirmStingEnabled,
  ]);

  // A9: when the QR cinematic is disabled, fire onComplete immediately
  // so the parent's result reveal happens without delay. The component
  // still renders an EMPTY backdrop with the testid so test assertions
  // that check for the testid presence keep working — the parent's
  // qrPending state controls mount/unmount, not the cinematic visibility.
  useEffect(() => {
    if (cinematicActive) return;
    onComplete();
  }, [cinematicActive, onComplete]);

  // Slice B: confirm-stage timer. For voice + QR 1, the card initially
  // shows "Confirmed — Excellent." for VOICE_QR1_CONFIRM_HOLD_MS and
  // then transitions to the quality_name + flavor string.
  const [confirmStageDone, setConfirmStageDone] = useState(!isVoiceQR1);
  useEffect(() => {
    if (!isVoiceQR1) {
      return;
    }
    const timer = setTimeout(() => {
      setConfirmStageDone(true);
    }, VOICE_QR1_CONFIRM_HOLD_MS);
    return () => clearTimeout(timer);
  }, [isVoiceQR1]);

  // Track whether the gold-leaf flash has completed so we don't trigger
  // onComplete twice when both flash + card animations settle.
  const [flashDone, setFlashDone] = useState(
    FLASH_INTENSITY_BY_QR[qr as 1 | 2] === undefined,
  );
  const [cardDone, setCardDone] = useState(false);
  const completeFiredRef = useRef(false);

  // 2026-06-05 cinematic-linger: in 'manual' mode the animation completing
  // does NOT dismiss the cinematic — only the user click below does.
  // Auto mode keeps the previous behavior (both flash + card animations
  // settle → fire onComplete). The completeFiredRef guard ensures a
  // single dispatch in either path.
  useEffect(() => {
    if (cinematicDismissMode === 'manual') return;
    if (flashDone && cardDone && !completeFiredRef.current) {
      completeFiredRef.current = true;
      onComplete();
    }
  }, [flashDone, cardDone, onComplete, cinematicDismissMode]);

  const handleManualDismiss = useCallback(() => {
    if (cinematicDismissMode !== 'manual') return;
    if (completeFiredRef.current) return;
    completeFiredRef.current = true;
    onComplete();
  }, [cinematicDismissMode, onComplete]);

  const flashIntensity =
    qr === 1 || qr === 2 ? FLASH_INTENSITY_BY_QR[qr] : undefined;
  // A9: flash overlay is suppressed when cinematic is disabled.
  const renderFlash = cinematicActive && flashIntensity !== undefined;

  // A9: when cinematic disabled, render a minimal placeholder backdrop
  // so DOM/testid assertions keep working but no visual fires.
  if (!cinematicActive) {
    return (
      <div
        className="br-qr-cinematic-backdrop br-qr-cinematic-disabled"
        data-testid={`${testidPrefix}-backdrop`}
        data-variant={variantKey}
        data-mode={mode}
        data-disabled="true"
        aria-hidden="true"
      />
    );
  }

  const isManualDismiss = cinematicDismissMode === 'manual';

  return (
    <div
      className={
        'br-qr-cinematic-backdrop' +
        (isManualDismiss ? ' br-qr-cinematic-backdrop--manual' : '')
      }
      data-testid={`${testidPrefix}-backdrop`}
      data-variant={variantKey}
      data-mode={mode}
      data-dismiss-mode={cinematicDismissMode}
      aria-live="polite"
      aria-label={`Roll outcome: ${roll.quality_name}${isManualDismiss ? ' — click to continue' : ''}`}
      onClick={isManualDismiss ? handleManualDismiss : undefined}
      // Keep cursor:pointer ONLY in manual mode so users see the
      // affordance. Auto mode leaves the backdrop click-inert (matches
      // 1.276.0 and earlier behavior).
      style={isManualDismiss ? { cursor: 'pointer', pointerEvents: 'auto' } : undefined}
      role={isManualDismiss ? 'button' : undefined}
      tabIndex={isManualDismiss ? 0 : undefined}
      onKeyDown={
        isManualDismiss
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleManualDismiss();
              }
            }
          : undefined
      }
    >
      {renderFlash && (
        <GoldLeafFlash
          onComplete={() => setFlashDone(true)}
          intensity={flashIntensity}
          durationMs={flashDurationMs}
        />
      )}
      <motion.div
        className="br-qr-cinematic-card"
        data-testid={testidPrefix}
        data-variant={variantKey}
        initial={{ opacity: 0, y: 24, scale: 0.94 }}
        animate={{
          opacity: [0, 1, 1, 1],
          y: [24, 0, 0, 0],
          scale: [0.94, 1.02, 1.0, 1.0],
        }}
        transition={{
          duration: durationSeconds,
          times: [0, 0.2, 0.6, 1],
          ease: 'easeOut',
        }}
        onAnimationComplete={() => setCardDone(true)}
      >
        {confirmText && !confirmStageDone ? (
          <div
            className="br-qr-cinematic-confirm"
            data-testid={`${testidPrefix}-confirm`}
          >
            {confirmText}
          </div>
        ) : (
          <>
            <div
              className="br-qr-cinematic-quality"
              data-testid={`${testidPrefix}-quality`}
            >
              {roll.quality_name}
            </div>
            {qrFlavorStringsEnabled && (
              <div
                className="br-qr-cinematic-flavor"
                data-testid={`${testidPrefix}-flavor`}
              >
                {flavor}
              </div>
            )}
            {deadpanLine && (
              <div
                className="br-qr-cinematic-deadpan"
                data-testid={`${testidPrefix}-deadpan`}
              >
                {deadpanLine}
              </div>
            )}
            {isManualDismiss && (
              <div
                className="br-qr-cinematic-dismiss-hint"
                data-testid={`${testidPrefix}-dismiss-hint`}
                aria-hidden="true"
              >
                Click to continue ▸
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}

export default QRCinematicResponse;
